import { useState, useRef, useCallback, useEffect } from 'react';
import { useAudioRecording } from './useAudioRecording';
import { transcribeAudio } from '../../../config/api';

const MAX_RECORDING_DURATION = 15000; // 15 seconds hard fallback

export function useSpeechRecognition() {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const stoppingRef = useRef(false); // prevent double-stop from silence + timer race
  const { startRecording, stopRecording, status, volume } = useAudioRecording();

  const isRecording = status === 'recording';
  const micPermissionDenied = status === 'error';

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stoppingRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const stopAndTranscribe = useCallback(async (resolve: (value: { transcript: string; micDenied: boolean }) => void) => {
    // Guard against double invocation (silence detection + max timer race)
    if (stoppingRef.current) return;
    stoppingRef.current = true;

    // Clear the max-duration timer if it's still running
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const blob = await stopRecording();
    if (!blob || !mountedRef.current) {
      stoppingRef.current = false;
      resolve({ transcript: '', micDenied: false });
      return;
    }

    setIsTranscribing(true);
    try {
      const result = await transcribeAudio(blob);
      if (mountedRef.current) {
        setTranscript(result);
        setIsTranscribing(false);
      }
      stoppingRef.current = false;
      resolve({ transcript: result, micDenied: false });
    } catch {
      if (mountedRef.current) {
        setIsTranscribing(false);
        setError('transcription_failed');
      }
      stoppingRef.current = false;
      resolve({ transcript: '', micDenied: false });
    }
  }, [stopRecording]);

  const startListening = useCallback(async (): Promise<{ transcript: string; micDenied: boolean }> => {
    setTranscript('');
    setError(null);
    stoppingRef.current = false;

    return new Promise<{ transcript: string; micDenied: boolean }>((resolve) => {
      const doStart = async () => {
        const started = await startRecording(() => {
          // Silence detected callback — stop and transcribe early
          stopAndTranscribe(resolve);
        });

        if (!started) {
          setError('microphone_unavailable');
          resolve({ transcript: '', micDenied: true });
          return;
        }

        // Hard fallback: auto-stop after max duration
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          stopAndTranscribe(resolve);
        }, MAX_RECORDING_DURATION);
      };

      doStart();
    });
  }, [startRecording, stopAndTranscribe]);

  const stopListeningEarly = useCallback(async (): Promise<string> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const blob = await stopRecording();
    if (!blob) return '';

    setIsTranscribing(true);
    try {
      const result = await transcribeAudio(blob);
      if (mountedRef.current) {
        setTranscript(result);
        setIsTranscribing(false);
      }
      return result;
    } catch {
      if (mountedRef.current) {
        setIsTranscribing(false);
        setError('transcription_failed');
      }
      return '';
    }
  }, [stopRecording]);

  return {
    startListening,
    stopListeningEarly,
    isRecording,
    isTranscribing,
    transcript,
    error,
    micPermissionDenied,
    volume,
  };
}
